import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';

export interface WelcomeMessageAnalyticsEvent {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: 'vendor' | 'client' | 'admin';
  businessName?: string;
  eventType: 'view' | 'dismiss';
  timestamp: string;
}

export function useWelcomeMessageAnalytics(messageId: string | null) {
  return useQuery({
    queryKey: ['welcome-message-analytics', messageId],
    queryFn: async (): Promise<WelcomeMessageAnalyticsEvent[]> => {
      if (!messageId) return [];

      // Fetch analytics events for this message
      const { data: analyticsData, error: analyticsError } = await supabase
        .from('welcome_message_analytics')
        .select('*')
        .eq('message_id', messageId)
        .order('created_at', { ascending: false });

      if (analyticsError) throw analyticsError;
      if (!analyticsData || analyticsData.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(analyticsData.map(e => e.user_id))];

      // Fetch profiles to get roles
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('user_id, role')
        .in('user_id', userIds);

      const profilesMap = new Map(profilesData?.map(p => [p.user_id, p]) || []);

      // Fetch client profiles
      const { data: clientProfilesData } = await supabase
        .from('client_profiles')
        .select('user_id, first_name, last_name')
        .in('user_id', userIds);

      const clientProfilesMap = new Map(clientProfilesData?.map(p => [p.user_id, p]) || []);

      // Fetch vendor profiles
      const { data: vendorProfilesData } = await supabase
        .from('vendor_profiles_enhanced')
        .select('user_id, first_name, last_name, business_name')
        .in('user_id', userIds);

      const vendorProfilesMap = new Map(vendorProfilesData?.map(p => [p.user_id, p]) || []);

      // Fetch emails using edge function
      let emailsMap = new Map<string, string>();
      try {
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.access_token) {
          const response = await invokeEdgeFunction('get-user-emails', {
            body: { userIds },
            callerOperation: 'useWelcomeMessageAnalytics.fetchEmails',
          });
          if (response.data) {
            const result = response.data as { emails?: Record<string, string> };
            if (result.emails) {
              emailsMap = new Map(Object.entries(result.emails));
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch user emails:', error);
      }

      // Build the events array
      const events: WelcomeMessageAnalyticsEvent[] = analyticsData.map(event => {
        const profile = profilesMap.get(event.user_id);
        const vendorProfile = vendorProfilesMap.get(event.user_id);
        const clientProfile = clientProfilesMap.get(event.user_id);
        
        let userName = 'Unknown User';
        let businessName: string | undefined;
        let role: 'vendor' | 'client' | 'admin' = 'client';

        // Determine role - prefer profiles table, but infer from profile existence
        if (profile?.role) {
          role = profile.role as 'vendor' | 'client' | 'admin';
        } else if (vendorProfile) {
          role = 'vendor';
        } else if (clientProfile) {
          role = 'client';
        }

        // Get user name and business name - check both profile types
        if (vendorProfile) {
          // For vendors: try first/last name, fall back to business name
          const fullName = [vendorProfile.first_name, vendorProfile.last_name]
            .filter(Boolean)
            .join(' ');
          userName = fullName || vendorProfile.business_name || 'Unknown Vendor';
          businessName = vendorProfile.business_name || undefined;
        } else if (clientProfile) {
          // For clients: use first/last name
          userName = [clientProfile.first_name, clientProfile.last_name]
            .filter(Boolean)
            .join(' ') || 'Unknown Client';
        }

        return {
          id: event.id,
          userId: event.user_id,
          userName,
          userEmail: emailsMap.get(event.user_id) || 'Email not available',
          userRole: role,
          businessName,
          eventType: event.event_type as 'view' | 'dismiss',
          timestamp: event.created_at,
        };
      });

      return events;
    },
    enabled: !!messageId,
  });
}
